"use client";
import { useEffect, useState } from "react";
import {
  isWithinRTH,
  marketSession,
  type MarketSession,
  type SessionAssetClass,
} from "@/lib/marketHours";

/**
 * Is the US equity market open RIGHT NOW (regular trading hours)?
 *
 * The single source of truth for every pane's "LIVE" badge. Computed from
 * the browser clock via the same `isWithinRTH()` the server uses (pure, no
 * provider call), and re-evaluated every 30 s so the badge flips at 9:30
 * open / 16:00 close without a page reload.
 *
 * Why this exists: badges used to gate on stale signals — a snapshot
 * payload's `isLive` flag (which freezes `true` after the 4 PM close, since
 * the worker stops rewriting it) or live-quote freshness (which stays true
 * on after-hours trades). Both showed "LIVE" long after the close. Liveness
 * is a property of NOW, not of the data, so it belongs in a clock-driven
 * hook.
 *
 * Starts `false` on first render to avoid an SSR/client hydration mismatch,
 * then corrects on mount.
 */
export function useMarketOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const update = () => setOpen(isWithinRTH());
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, []);
  return open;
}

/**
 * The four-state session — "pre-market" | "live" | "post-market" |
 * "closed" — for the LIVE badge. Same clock-driven, 30 s-refreshed approach
 * as useMarketOpen; starts "closed" to avoid a hydration mismatch.
 */
export function useMarketSession(
  asset: SessionAssetClass = "us-equity",
): MarketSession {
  const [session, setSession] = useState<MarketSession>("closed");
  useEffect(() => {
    const update = () => setSession(marketSession(Date.now(), asset));
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, [asset]);
  return session;
}
